import { Component, OnInit, OnDestroy, inject, effect } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { TopCategoriesComponent } from '../top-categories/top-categories.component';
import { DropdownComponent } from '../../shared/components/dropdown/dropdown.component';
import { AdminService } from '../../core/services/admin.service';
import { SessionService } from '../../core/services/session.service';
import { AuthenticationService } from '../../features/auth/services/auth.service';
import { ImpersonationOption } from '../../core/models/admin.model';

@Component({
  selector: 'app-top',
  templateUrl: './top.component.html',
  styleUrls: ['./top.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    NgIf,
    TopCategoriesComponent,
    DropdownComponent,
  ],
})
export class TopComponent implements OnInit, OnDestroy {
  private sessionService = inject(SessionService);
  private adminService = inject(AdminService);
  private authService = inject(AuthenticationService);

  customers: ImpersonationOption[] = [];
  selectedCardCode: string | null = null;
  userEmail: string = '';
  isAdmin: boolean = false;
  selectedCompanyName: string | null = null;
  private optionsLoaded = false;
  isLoadingOptions = false;
  dropdownDefaultTitle = 'ALL';

  constructor(public router: Router) {
    // React to logout only - clear data when user logs out
    effect(() => {
      const isLoggedIn = this.sessionService.loggedIn();
      if (!isLoggedIn) {
        this.userEmail = '';
        this.isAdmin = false;
        this.customers = [];
        this.selectedCardCode = null;
        this.selectedCompanyName = null;
        this.optionsLoaded = false;
        this.isLoadingOptions = false;
        this.dropdownDefaultTitle = 'ALL';
        this.adminService.clearImpersonation();
      }
    }, { allowSignalWrites: true });

    // React to login success - load data when user logs in
    effect(() => {
      const loginSuccess = this.authService.loginSuccess();
      if (loginSuccess) {
        // Session data is already stored when loginSuccess is emitted
        this.loadUserData();
      }
    }, { allowSignalWrites: true });

    // React to impersonation changes (e.g., Dashboard resetting to "ALL")
    effect(() => {
      const companyCode = this.adminService.currentCompanyCode();
      // Only react if admin
      if (this.isAdmin) {
        this.selectedCardCode = companyCode;
        if (companyCode === null || companyCode === '') {
          this.dropdownDefaultTitle = 'ALL';
          this.selectedCompanyName = null;
        } else {
          const storedName = sessionStorage.getItem('impersonation_company_name');
          this.dropdownDefaultTitle = storedName || companyCode;
          this.selectedCompanyName = storedName || companyCode;
        }
      }
    });
  }

  ngOnInit() {
    // Load data on init (for page refresh when already logged in)
    if (this.sessionService.loggedIn()) {
      this.loadUserData();
    }
  }

  ngOnDestroy() {
    // Cleanup handled by Angular's effect cleanup
  }

  private loadUserData(): void {
    this.userEmail = sessionStorage.getItem('user_email') || '';
    this.isAdmin = sessionStorage.getItem('user_role')?.toLowerCase() === 'admin';

    // Reset dropdown to default before loading
    this.dropdownDefaultTitle = 'ALL';
    this.selectedCardCode = null;
    this.selectedCompanyName = null;

    if (this.isAdmin) {
      // Restore within-tab state on page refresh (sessionStorage persists across refreshes)
      // storedCardCode can be: null (not set), "" (ALL), or "BP123" (specific company)
      const storedCardCode = sessionStorage.getItem('impersonation_company_code');
      const storedCompanyName = sessionStorage.getItem('impersonation_company_name');

      // Convert empty string to null to match the "ALL" option from server
      this.selectedCardCode = storedCardCode === '' || storedCardCode === null ? null : storedCardCode;

      // Show stored company name while loading options
      if (storedCompanyName) {
        this.dropdownDefaultTitle = storedCompanyName;
      }

      // If there's a selected company, load options to display the company name
      if (this.selectedCardCode) {
        this.loadImpersonationOptions();
      }
    }
  }

  onDropdownOpen(): void {
    // Load options only once when dropdown is opened for the first time
    if (!this.optionsLoaded && this.isAdmin) {
      this.loadImpersonationOptions();
    }
  }

  private loadImpersonationOptions(): void {
    this.isLoadingOptions = true;
    this.adminService.getImpersonationOptions().subscribe({
      next: (response) => {
        // Add displayName field: "{cardCode} - {name}" or just "ALL" for the first option
        this.customers = response.options.map(option => ({
          ...option,
          displayName: option.cardCode ? `${option.cardCode} - ${option.name}` : option.name
        }));
        this.optionsLoaded = true;
        this.isLoadingOptions = false;
      },
      error: (error) => {
        console.error('Error loading impersonation options:', error);
        this.isLoadingOptions = false;
      }
    });
  }

  onImpersonationSelect(selectedOption: ImpersonationOption): void {
    // cardCode empty string means "ALL" (null in API)
    const newCardCode = selectedOption.cardCode || null;

    // Get previous value from sessionStorage (ngModel already updated selectedCardCode)
    const storedCardCode = sessionStorage.getItem('impersonation_company_code');
    // storedCardCode can be: null (not set), "" (ALL), or "BP123" (specific company)
    const previousCardCode = storedCardCode === '' || storedCardCode === null ? null : storedCardCode;

    // Don't do anything if the same company is selected
    if (newCardCode === previousCardCode) {
      return;
    }

    // Get sessionId from signal or sessionStorage
    const sessionId = this.adminService.currentSessionId() ?? sessionStorage.getItem('impersonation_session_id');

    if (!sessionId) {
      console.error('No impersonation session found');
      return;
    }

    this.adminService.setImpersonation(sessionId, newCardCode).subscribe({
      next: () => {
        this.selectedCardCode = selectedOption.cardCode;
        this.selectedCompanyName = selectedOption.name;

        // Save display name to session for quick display on reload
        // Format: "{cardCode} - {name}" or just "ALL"
        const displayName = selectedOption.cardCode
          ? `${selectedOption.cardCode} - ${selectedOption.name}`
          : selectedOption.name;

        if (displayName && displayName !== 'ALL') {
          sessionStorage.setItem('impersonation_company_name', displayName);
        } else {
          sessionStorage.removeItem('impersonation_company_name');
        }

        // Set flag to indicate user intentionally selected a company from dropdown
        // This prevents Dashboard from resetting impersonation on reload
        sessionStorage.setItem('user_selected_company', 'true');

        // Redirect to dashboard and reload to refresh data for the new company
        window.location.href = '/';
      },
      error: (error) => {
        console.error('Error setting impersonation:', error);
      }
    });
  }
}
